// 2Pay.js card-form style objects.
//
// NOTE: 2Pay.js replaces the entire default style when you pass a `style`
// object — it does NOT merge. Passing a sparse object breaks the iframe
// layout. The `defaultStyle` here is a verbatim copy of the 2Checkout
// "Default style object" documented at:
//   https://verifone.cloud/docs/2checkout/API-Integration/2Pay.js-payments-solution/
//     2Pay.js-Payments-Solution-Integration-Guide/How_to_customize_and_style_the_2Pay.js_payment_form
//
// Theme tweaks (`lightStyle` / `darkStyle`) spread over `defaultStyle` to
// change only the colors we care about — keeps every layout rule intact.
import type { TwoPayCardStyle } from '../twopay.d';

const defaultStyle: TwoPayCardStyle = {
  margin: 0,
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '1rem',
  fontWeight: '400',
  lineHeight: '1.5',
  color: '#212529',
  textAlign: 'left',
  backgroundColor: '#ffffff',
  '*': { boxSizing: 'border-box' },
  '.no-gutters': { marginRight: 0, marginLeft: 0 },
  '.row': { display: 'flex', flexWrap: 'wrap' },
  '.col': {
    flexBasis: '0',
    flexGrow: '1',
    maxWidth: '100%',
    padding: '0',
    position: 'relative',
    width: '100%',
  },
  div: { display: 'block' },
  '.field-container': { paddingBottom: '14px' },
  '.field-wrapper': { paddingRight: '25px' },
  '.input-wrapper': { position: 'relative' },
  label: {
    display: 'inline-block',
    marginBottom: '9px',
    color: '#313131',
    fontSize: '14px',
    fontWeight: '300',
    lineHeight: '17px',
  },
  input: {
    overflow: 'visible',
    margin: 0,
    fontFamily: 'inherit',
    display: 'block',
    width: '100%',
    height: '42px',
    padding: '10px 12px',
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: '22px',
    color: '#313131',
    backgroundColor: '#fff',
    backgroundClip: 'padding-box',
    border: '1px solid #CBCBCB',
    borderRadius: '3px',
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
    outline: 0,
  },
  'input:focus': { border: '1px solid #5D5D5D', backgroundColor: '#FFFDF2' },
  // Suppress the default `.is-error input` red border while the field is
  // still empty — it fires on mount and looks like a validation failure
  // even before the user interacts. Real errors (typing invalid data)
  // still show red via `.is-error:not(.is-empty)` below.
  '.is-error input': { border: '1px solid #CBCBCB' },
  '.is-error input:focus': { backgroundColor: '#FFFDF2' },
  '.is-error:not(.is-empty) input': { border: '1px solid #D9534F' },
  '.is-error:not(.is-empty) input:focus': { backgroundColor: '#D9534F0B' },
  '.is-valid input': { border: '1px solid #1BB43F' },
  '.is-valid input:focus': { backgroundColor: '#1BB43F0B' },
  '.validation-message': {
    color: '#D9534F',
    fontSize: '10px',
    fontStyle: 'italic',
    marginTop: '6px',
    marginBottom: '-5px',
    display: 'block',
    lineHeight: '1',
  },
  '.card-expiration-date': { paddingRight: '.5rem' },
  '.is-empty input': { color: '#EBEBEB' },
  '.lock-icon': { top: 'calc(50% - 7px)', right: '10px' },
  '.valid-icon': { top: 'calc(50% - 8px)', right: '-25px' },
  '.error-icon': { top: 'calc(50% - 8px)', right: '-25px' },
  '.card-icon': { top: 'calc(50% - 10px)', left: '10px', display: 'none' },
  '.is-empty .card-icon': { display: 'block' },
  '.is-focused .card-icon': { display: 'none' },
  '.card-type-icon': { right: '30px', display: 'block' },
  '.card-type-icon.visa': { top: 'calc(50% - 14px)' },
  '.card-type-icon.mastercard': { top: 'calc(50% - 14.5px)' },
  '.card-type-icon.amex': { top: 'calc(50% - 14px)' },
  '.card-type-icon.discover': { top: 'calc(50% - 14px)' },
  '.card-type-icon.jcb': { top: 'calc(50% - 14px)' },
  '.card-type-icon.dankort': { top: 'calc(50% - 14px)' },
  '.card-type-icon.cartebleue': { top: 'calc(50% - 14px)' },
  '.card-type-icon.diners': { top: 'calc(50% - 14px)' },
  '.card-type-icon.elo': { top: 'calc(50% - 14px)' },
};

const lightStyle: TwoPayCardStyle = {
  ...defaultStyle,
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
  color: '#111827',
  backgroundColor: '#ffffff',
  label: {
    ...(defaultStyle.label as TwoPayCardStyle),
    color: '#374151',
    fontWeight: '500',
  },
  input: {
    ...(defaultStyle.input as TwoPayCardStyle),
    color: '#111827',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
  },
  'input:focus': { border: '1px solid #6366f1', backgroundColor: '#ffffff' },
};

const darkStyle: TwoPayCardStyle = {
  ...defaultStyle,
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
  color: '#f3f4f6',
  backgroundColor: '#1f2937',
  label: {
    ...(defaultStyle.label as TwoPayCardStyle),
    color: '#d1d5db',
    fontWeight: '500',
  },
  input: {
    ...(defaultStyle.input as TwoPayCardStyle),
    color: '#f3f4f6',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '6px',
  },
  'input:focus': { border: '1px solid #818cf8', backgroundColor: '#111827' },
};

export function cardStyle(isDark: boolean): TwoPayCardStyle {
  return isDark ? darkStyle : lightStyle;
}
